import { useCurrentOrganization } from '@/shared/hooks/api/organization'
import { useTranslation } from 'react-i18next'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { formatAddressFields } from '@/features/associated-parties/formatAddress'
import { getOnboardingTermsDeMarkdown } from '@/features/onboarding/onboarding-terms-de'
import { getOnboardingTermsEnMarkdown } from '@/features/onboarding/onboarding-terms-en'
import { getOnboardingTermsZhMarkdown } from '@/features/onboarding/onboarding-terms-zh'

type OnboardingTermsStepProps = {
  isBusy: boolean
  onBack: () => void
  onComplete: () => void | Promise<void>
}

function isChineseInterfaceLanguage(language: string): boolean {
  return language === 'zh-CN' || language.startsWith('zh')
}

function isGermanInterfaceLanguage(language: string): boolean {
  return language === 'de' || language.startsWith('de-')
}

function getTermsDocumentLang(language: string): string {
  if (isChineseInterfaceLanguage(language)) {
    return 'zh-CN'
  }
  if (isGermanInterfaceLanguage(language)) {
    return 'de'
  }
  return 'en'
}

export function OnboardingTermsStep({
  isBusy,
  onBack,
  onComplete,
}: OnboardingTermsStepProps) {
  const { t, i18n } = useTranslation('onboarding')
  const { t: tAddress } = useTranslation('associatedParties')
  const { t: tCountry } = useTranslation('countries')
  const { data: organization } = useCurrentOrganization()
  const formattedAddress =
    organization === undefined
      ? ''
      : formatAddressFields(
          {
            street: organization.street,
            streetNumber: organization.streetNumber,
            street2: organization.street2,
            postCode: organization.postCode,
            city: organization.city,
            subDivision: organization.subDivision,
            country: organization.country,
            poBox: organization.poBox,
          },
          {
            getCountryName: (code) => tCountry(code) || code,
            translate: (key, options) => tAddress(key, options),
          }
        )
  const organizationName = organization?.name ?? ''
  const organizationAddress = formattedAddress
  const termsArgs = { organizationName, organizationAddress }
  let termsBody: string
  if (isChineseInterfaceLanguage(i18n.language)) {
    termsBody = getOnboardingTermsZhMarkdown(termsArgs)
  } else if (isGermanInterfaceLanguage(i18n.language)) {
    termsBody = getOnboardingTermsDeMarkdown(termsArgs)
  } else {
    termsBody = getOnboardingTermsEnMarkdown(termsArgs)
  }
  return (
    <>
      <Card className='space-y-6'>
        <CardContent>
          <ScrollArea className='h-[500px]'>
            <div
              className='prose prose-sm max-w-none px-1 py-2 pr-4 dark:prose-invert'
              lang={getTermsDocumentLang(i18n.language)}
            >
              <Markdown remarkPlugins={[remarkGfm]}>{termsBody}</Markdown>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      <div className='flex justify-between py-6'>
        <Button
          type='button'
          variant='outline'
          disabled={isBusy}
          onClick={onBack}
        >
          {t('back')}
        </Button>
        <Button
          type='button'
          disabled={isBusy}
          onClick={() => {
            void onComplete()
          }}
        >
          {t('acceptTermsAndCompleteSetup')}
        </Button>
      </div>
    </>
  )
}
